'use client'

// Horizontal 10-week experimental timeline. Drawn purely in SVG so it
// scales cleanly. Used in Section B between the two impact pillars to
// give the metrics temporal context.

const OLIVE = '#5B7005'
const LIME  = '#9FAF08'
const EARTH = '#513205'
const EARTH_50 = 'rgba(81,50,5,0.5)'

interface Milestone {
  week: number          // 0–10
  label: string
}

interface Props {
  title: string
  milestones: Milestone[]
}

export default function ProcessTimeline({ title, milestones }: Props) {
  const W = 720
  const H = 120
  const padX = 32
  const trackY = 50
  const trackLen = W - padX * 2
  const weekX = (w: number) => padX + (w / 10) * trackLen

  return (
    <div style={{ margin: 'clamp(40px, 6vh, 64px) 0', maxWidth: 720 }}>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 11,
        letterSpacing: '0.36em', textTransform: 'uppercase',
        fontWeight: 600, color: OLIVE,
        marginBottom: 18,
      }}>
        {title}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        aria-hidden="true"
      >
        {/* Track baseline */}
        <line
          x1={padX} y1={trackY}
          x2={W - padX} y2={trackY}
          stroke="rgba(81,50,5,0.25)"
          strokeWidth={1}
        />
        {/* Animated lime progress fill */}
        <line
          x1={padX} y1={trackY}
          x2={W - padX} y2={trackY}
          stroke={LIME}
          strokeWidth={2}
          strokeDasharray={trackLen}
          strokeDashoffset={trackLen}
          style={{
            animation: 'timelineDraw 1.6s 0.2s ease-out forwards',
          }}
        />

        {/* Week tick marks (every week) + labels (every 2 weeks) */}
        {Array.from({ length: 11 }).map((_, w) => {
          const x = weekX(w)
          const isMajor = w % 2 === 0
          return (
            <g key={w}>
              <line
                x1={x} y1={trackY - (isMajor ? 6 : 3)}
                x2={x} y2={trackY + (isMajor ? 6 : 3)}
                stroke={isMajor ? OLIVE : 'rgba(81,50,5,0.4)'}
                strokeWidth={isMajor ? 1.2 : 0.8}
              />
              {isMajor && (
                <text
                  x={x}
                  y={trackY + 22}
                  textAnchor="middle"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 9,
                    letterSpacing: '0.18em',
                    fontWeight: 600,
                    fill: EARTH_50,
                    textTransform: 'uppercase',
                  }}
                >
                  W{String(w).padStart(2, '0')}
                </text>
              )}
            </g>
          )
        })}

        {/* Milestone callouts */}
        {milestones.map((m, i) => {
          const x = weekX(m.week)
          const yLabel = trackY - 22
          return (
            <g key={i}>
              {/* Vertical leader line */}
              <line
                x1={x} y1={trackY - 8}
                x2={x} y2={yLabel + 4}
                stroke={LIME}
                strokeWidth={1}
                opacity={0.6}
              />
              {/* Milestone dot on track */}
              <circle cx={x} cy={trackY} r={4} fill={LIME} />
              <circle cx={x} cy={trackY} r={7} fill="none" stroke={LIME} strokeWidth={0.7} opacity={0.4} />
              {/* Label */}
              <text
                x={x}
                y={yLabel}
                textAnchor="middle"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontStyle: 'italic',
                  fontSize: 12,
                  fill: EARTH,
                  letterSpacing: '-0.005em',
                }}
              >
                {m.label}
              </text>
            </g>
          )
        })}
      </svg>
      <style>{`
        @keyframes timelineDraw {
          to { stroke-dashoffset: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          line[stroke-dashoffset] { stroke-dashoffset: 0 !important; animation: none !important; }
        }
      `}</style>
    </div>
  )
}
